import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ArrowLeft, User, Briefcase, Clock, MapPin, Calendar, FileText,
  TrendingUp, AlertTriangle, Download, CalendarDays, Timer, ShieldAlert, ShieldCheck,
} from 'lucide-react';

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchEmployee();
  }, [employeeId]);

  useEffect(() => {
    fetchStats();
  }, [employeeId, month]);

  const fetchEmployee = async () => {
    try {
      const res = await api.get(`/employees/${employeeId}`);
      setEmployee(res.data);
    } catch (error) {
      toast.error('Employee not found');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`/employees/${employeeId}/stats?month=${month}`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const formatTime = (t) => {
    if (!t) return '-';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDuration = (mins) => {
    if (!mins || mins <= 0) return '0min';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}hr ${m}min` : `${m}min`;
  };

  const downloadPayslipPDF = async (payslipId, pMonth) => {
    try {
      const res = await api.get(`/payroll/${payslipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip-${employee?.name?.replace(/ /g, '_')}-${pMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Download failed'); }
  };

  const downloadUploadedPayslip = async (u) => {
    try {
      const res = await api.get(`/payslip-uploads/${u.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip-${employee?.name?.replace(/ /g, '_')}-${u.month}.${u.filename.split('.').pop()}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-slate-500">Loading...</p></div>;
  if (!employee) return null;

  const s = stats || {};
  const efficiency = s.working_days && s.present_days ? Math.round((s.present_days / s.working_days) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-profile-page">
      {/* Back + Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/employees')} className="mb-3 gap-1.5 text-slate-500" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Employees
        </Button>
        <div className="bg-gradient-to-r from-[#FF9933] via-white to-[#138808] rounded-xl p-[2px]">
          <div className="bg-white rounded-xl p-5">
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                {employee.photo_url ? (
                  <img src={employee.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-slate-400" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-slate-800">{employee.name}</h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Code: <strong className="text-[#FF9933]">{employee.employee_code}</strong></span>
                  <Badge className={employee.is_active !== false ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                    {employee.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {employee.location_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {employee.shift_start} - {employee.shift_end}</span>
                  {employee.email && <span>{employee.email}</span>}
                  {employee.phone && <span>{employee.phone}</span>}
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-sm">
                  <span>Basic: <strong>Rs. {(employee.salary_basic || 0).toLocaleString()}</strong></span>
                  <span>DA: <strong>Rs. {(employee.salary_da || 0).toLocaleString()}</strong></span>
                  <span>HRA: <strong>Rs. {(employee.salary_hra || 0).toLocaleString()}</strong></span>
                  <span>Paid Leave Bal: <strong className="text-blue-600">{employee.paid_leave_balance ?? 0}</strong></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Month Picker */}
      <div className="flex items-center gap-3">
        <Calendar className="w-4 h-4 text-slate-400" />
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="profile-month-picker" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="bg-white border shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-500 mb-1">Working Days</p>
          <p className="text-xl font-bold">{s.working_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-500 mb-1">Present</p>
          <p className="text-xl font-bold text-[#138808]">{s.present_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-500 mb-1">Absent</p>
          <p className="text-xl font-bold text-red-500">{s.absent_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-500 mb-1">Total Hours</p>
          <p className="text-xl font-bold">{formatDuration(s.total_hours)}</p>
        </CardContent></Card>
        <Card className="bg-white border shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-500 mb-1">Attendance</p>
          <p className={`text-xl font-bold ${efficiency >= 80 ? 'text-[#138808]' : efficiency >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>{efficiency}%</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4">
          <p className="text-xs text-blue-600 mb-1">Paid Leave</p>
          <p className="text-xl font-bold text-blue-700">{s.paid_leave_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-yellow-50 border-yellow-200"><CardContent className="p-4">
          <p className="text-xs text-yellow-600 mb-1">Unpaid Leave</p>
          <p className="text-xl font-bold text-yellow-700">{s.unpaid_leave_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-purple-50 border-purple-200"><CardContent className="p-4">
          <p className="text-xs text-purple-600 mb-1">Late Days</p>
          <p className="text-xl font-bold text-purple-700">{s.late_days || 0}</p>
        </CardContent></Card>
        <Card className="bg-green-50 border-green-200"><CardContent className="p-4">
          <p className="text-xs text-green-600 mb-1">Overtime</p>
          <p className="text-xl font-bold text-green-700">{s.overtime_days || 0} days / {formatDuration(s.overtime_mins)}</p>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="mb-4" data-testid="profile-tabs">
          <TabsTrigger value="attendance" className="gap-1.5"><CalendarDays className="w-4 h-4" />Attendance</TabsTrigger>
          <TabsTrigger value="payslips" className="gap-1.5"><FileText className="w-4 h-4" />Payslips</TabsTrigger>
          <TabsTrigger value="leaves" className="gap-1.5"><Timer className="w-4 h-4" />Leaves</TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="profile-attendance-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Check In</TableHead>
                      <TableHead className="font-semibold">Check Out</TableHead>
                      <TableHead className="font-semibold">Hours</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Range</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!s.attendance_records?.length ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No records</TableCell></TableRow>
                    ) : s.attendance_records.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.date}</TableCell>
                        <TableCell className="text-green-600">{formatTime(r.check_in)}</TableCell>
                        <TableCell className={r.check_out ? '' : 'text-[#FF9933] font-medium'}>{r.check_out ? formatTime(r.check_out) : 'Active'}</TableCell>
                        <TableCell>{r.hours_worked || '-'}</TableCell>
                        <TableCell>
                          {r.is_late ? (
                            <Badge variant="destructive" className="text-xs">Late {r.late_minutes > 0 ? formatDuration(r.late_minutes) : ''}</Badge>
                          ) : (
                            <Badge className="bg-[#138808] hover:bg-[#107307] text-xs">On Time</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{r.check_in_area || r.location_name || '-'}</TableCell>
                        <TableCell>
                          {r.check_in_out_of_range ? (
                            <Badge variant="destructive" className="text-xs flex items-center gap-1 w-fit"><ShieldAlert className="w-3 h-3" />{r.check_in_geofence_distance}m</Badge>
                          ) : r.check_in_geofence_distance != null ? (
                            <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100 flex items-center gap-1 w-fit"><ShieldCheck className="w-3 h-3" />{r.check_in_geofence_distance}m</Badge>
                          ) : <span className="text-slate-400 text-xs">-</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payslips Tab */}
        <TabsContent value="payslips">
          {/* Uploaded Payslips */}
          {s.uploaded_payslips?.length > 0 && (
            <Card className="bg-white border shadow-sm mb-4">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF9933]" />Uploaded Payslips</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {s.uploaded_payslips.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-2.5 px-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="text-sm font-medium">{u.month}</p>
                          <p className="text-xs text-slate-500">{u.filename}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadUploadedPayslip(u)}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generated Payslips */}
          <Card className="bg-white border shadow-sm">
            <CardHeader><CardTitle className="text-lg">Generated Payslips</CardTitle></CardHeader>
            <CardContent>
              {s.all_payslips?.length > 0 ? (
                <div className="space-y-2">
                  {s.all_payslips.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2.5 px-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.month}</p>
                          <p className="text-xs text-slate-500">Gross: Rs. {p.gross_salary?.toLocaleString()} | Net: Rs. {p.net_salary?.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-green-600">Rs. {p.net_salary?.toLocaleString()}</span>
                        <Button size="sm" variant="outline" onClick={() => downloadPayslipPDF(p.id, p.month)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-500 text-center py-8">No payslips generated</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves">
          <Card className="bg-white border shadow-sm">
            <CardHeader><CardTitle className="text-lg">Leave History</CardTitle></CardHeader>
            <CardContent>
              {s.leaves?.length > 0 ? (
                <div className="space-y-2">
                  {s.leaves.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2.5 px-3 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{l.start_date} to {l.end_date} ({l.days} day{l.days > 1 ? 's' : ''})</p>
                        <p className="text-xs text-slate-500">{l.reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={l.leave_type === 'paid' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'}>
                          {l.leave_type}
                        </Badge>
                        <Badge className={
                          l.status === 'approved' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                          l.status === 'rejected' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                          'bg-slate-100 text-slate-600 hover:bg-slate-100'
                        }>{l.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-500 text-center py-8">No leave requests</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payslip Summary Card */}
      {s.payslip && (
        <Card className="bg-white border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#FF9933]" />Payslip Summary - {month}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Gross Salary</p>
                <p className="text-lg font-bold">Rs. {s.payslip.gross_salary?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-500">Deductions</p>
                <p className="text-lg font-bold text-red-600">Rs. {s.payslip.total_deductions?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600">Net Salary</p>
                <p className="text-lg font-bold text-green-700">Rs. {s.payslip.net_salary?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-500">Absence Deduction</p>
                <p className="text-lg font-bold text-blue-700">Rs. {s.payslip.deduction_absence?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
